/* eslint-disable i18next/no-literal-string */
import React, { ReactNode, useState } from 'react';
import styled, { css } from 'styled-components';

import useTranslation from 'hooks/use-translation';

import { removeTypename } from 'shared/helpers/utils';
import { dispensaryHoursForDay } from 'shared/helpers/dispensaries';
import { getEnabledV2OrderTypes } from 'shared/core/helpers/dispensaries';
import { orderTypesToDisplayNamesV2, orderTypesToShortNamesV2 } from 'shared/constants';
import { daysLongToShortNames as days } from 'shared/constants/time';
import { DesktopOnly, MobileOnly, Select } from 'shared/components';

import { paragraph } from 'components/core/typography';

import { Tabs, HorizontalTab } from 'components/core';

import { GqlDispensaries, GqlDispensaryHoursSettings, GqlHoursSettingsForOrderType } from 'types/graphql';

type OrderingHoursV2Props = {
  dispensary: GqlDispensaries;
  children: ReactNode;
};

type OrderTypeString = string & keyof GqlDispensaryHoursSettings;

export function OrderingHoursV2(props: OrderingHoursV2Props): JSX.Element {
  const { dispensary, children } = props;
  const { status } = dispensary;
  const hoursSettings = dispensary.hoursSettings ?? {};
  const enabledV2OrderTypes = getEnabledV2OrderTypes(dispensary);
  const initialOrderType = enabledV2OrderTypes[0] as OrderTypeString;

  const { t } = useTranslation();
  const [selectedOrderType, setSelectedOrderType] = useState(initialOrderType);

  const hoursForOrderType: GqlHoursSettingsForOrderType = removeTypename(hoursSettings[selectedOrderType]) ?? {};
  const effectiveHoursForOrderType = hoursForOrderType.effectiveHours ?? [];
  const closedText = t('storeInfo.closed', 'Closed');

  const handleOrderTypeChange = (e: React.ChangeEvent<HTMLInputElement>, value: string): void => {
    const newValue = e.target.value || value;
    setSelectedOrderType(newValue as OrderTypeString);
  };

  return (
    <OrderingHoursContainer>
      <HoursMenu>
        <DesktopOnly>
          <HoursTabs orientation='horizontal' value={selectedOrderType} onChange={handleOrderTypeChange}>
            {enabledV2OrderTypes.map((orderType: string) => (
              <HoursTab
                key={`hours-display-${orderType}`}
                label={orderTypesToShortNamesV2[orderType]}
                value={orderType}
              />
            ))}
          </HoursTabs>
        </DesktopOnly>
        <MobileOnly>
          <HoursSelect onChange={handleOrderTypeChange}>
            {enabledV2OrderTypes.map((orderType: string) => (
              <option key={`hours-display-${orderType}`} value={orderType}>
                {orderTypesToDisplayNamesV2[orderType]}
              </option>
            ))}
          </HoursSelect>
        </MobileOnly>
      </HoursMenu>
      <HoursContent>
        {children}
        {Object.keys(days).map((day) => {
          const abbrevDay = days[day];
          return (
            <OrderingHoursTable cellSpacing={0}>
              <TableRow key={abbrevDay}>
                <BodyCell firstColumn>{`${day}:`}</BodyCell>
                <BodyCell>
                  {status !== 'open' ? closedText : dispensaryHoursForDay(effectiveHoursForOrderType[day])}
                </BodyCell>
              </TableRow>
            </OrderingHoursTable>
          );
        })}
      </HoursContent>
    </OrderingHoursContainer>
  );
}

export const OrderingHoursContainer = styled.div`
  width: 95%;
  max-width: 455px;
`;

const OrderingHoursTable = styled.table`
  table-layout: fixed;
`;

const HoursMenu = styled.div`
  margin-bottom: 20px;
`;

const HoursSelect = styled(Select)`
  margin-top: 10px;
`;

const HoursTabs = styled(Tabs)`
  .MuiTabs-flexContainer {
    max-width: 430px;
  }

  .MuiTab-root {
    padding: 0;
    margin-right: 9%;

    ${({ theme }) => theme.breakpoints.up(`lg`)} {
      margin-right: 12%;
    }
  }

  .MuiTabs-indicator {
    background-color: ${({ theme }) => theme.colors.primaryBlack};
  }
`;

const HoursTab = styled(HorizontalTab)`
  .MuiTab-wrapper {
    color: ${({ selected, theme }) => (selected ? theme.colors.primaryBlack : theme.colors.grey[45])};
    font-weight: ${({ selected }) => (selected ? `bold` : `normal`)};

    &:hover {
      color: ${({ theme }) => theme.colors.primaryBlack};
    }
  }
`;

const HoursContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 0%;
  min-width: 0;
`;

const TableRow = styled.tr`
  display: flex;
  justify-content: space-between;
  white-space: nowrap;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    display: block;
  }
`;

const cellStyles = css`
  max-width: 120px;
  width: 100%;
  display: inline-flex;
  align-items: center;
  padding: 8px 0;

  ${({ firstColumn }) =>
    firstColumn
      ? `
    font-weight: bold;
    max-width: 80px;
  `
      : `
    max-width: 120px;
  `}

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    max-width: ${({ firstColumn }) => (firstColumn ? '140px' : '125px')};
  }
`;

const BodyCell = styled.td`
  ${cellStyles}
  ${paragraph}
`;
